/**
 * Scopa bot.
 *
 * Every legal play (each card with each capture it may make, or trailing it) is valued by what it scores and what it
 * gives away:
 *  - Captured cards are worth a little each, diamonds, sevens and aces more, and the seven of diamonds a lot; a scopa is
 *    worth a point.
 *  - Trailing a valuable card is a loss, and leaving a table the next player could sweep with one card is a risk.
 *    NORMAL and above remember the cards played to estimate that risk; weaker bots ignore it.
 * The bot only ever reads its own seat's view.
 */
import { buildDeck, parseCard, type CardId } from '../../core/cards.js';
import { chance, type BotContext, type BotEngine } from '../../bots/types.js';
import { scopaValue, type ScopaAction, type ScopaLegal, type ScopaView } from './engine.js';

/** How much a card is worth to hold at the end of the deal. */
function cardWorth(card: CardId): number {
  if (card === '7D') return 8;
  const v = scopaValue(card);
  let w = 1;
  if (parseCard(card).suit === 'D') w += 1.5;
  if (v === 7) w += 1.5;
  else if (v === 6) w += 0.8;
  else if (v === 1) w += 0.5;
  return w;
}

/** Chance that the next player holds a card of value `v` among their three. */
function sweepRisk(view: ScopaView, tableLeft: readonly CardId[], ctx: BotContext): number {
  if (tableLeft.length === 0) return 0;
  const sum = tableLeft.reduce((n, c) => n + scopaValue(c), 0);
  if (sum > 10) return 0;
  if (ctx.profile.memory <= 0) return 0.25;
  const seen = new Set<CardId>([...view.hand, ...view.table]);
  // Captured cards are not listed in the view, so unseen is an upper bound on what the opponents may hold.
  const unseen = buildDeck().filter((c) => scopaValueOk(c) && !seen.has(c));
  const matching = unseen.filter((c) => scopaValue(c) === sum).length;
  return Math.min(1, (3 * matching) / Math.max(1, unseen.length));
}

const scopaValueOk = (c: CardId): boolean => {
  const r = parseCard(c).rank;
  return r !== 8 && r !== 9 && r !== 10;
};

interface Candidate {
  card: CardId;
  capture: CardId[];
  score: number;
}

function candidates(view: ScopaView, legal: ScopaLegal, ctx: BotContext): Candidate[] {
  const out: Candidate[] = [];
  const lastPlay = view.deckCount === 0 && view.handCounts.reduce((a, b) => a + b, 0) === 1;
  for (const m of legal.moves) {
    if (m.captures.length === 0) {
      // Trail: costs the card's worth and may leave a table the next player can sweep.
      const left = [...view.table, m.card];
      out.push({ card: m.card, capture: [], score: -cardWorth(m.card) * 0.8 - 4 * sweepRisk(view, left, ctx) });
      continue;
    }
    for (const cap of m.captures) {
      const left = view.table.filter((c) => !cap.includes(c));
      let score = cap.reduce((n, c) => n + cardWorth(c), 0) + cardWorth(m.card);
      if (left.length === 0 && !lastPlay) score += 5;
      else score -= 4 * sweepRisk(view, left, ctx);
      // Prefer capturing more cards when the value is level.
      score += 0.1 * cap.length;
      out.push({ card: m.card, capture: cap, score });
    }
  }
  return out;
}

export const scopaBot: BotEngine<ScopaView, ScopaLegal, ScopaAction> = {
  decideAction(view, legal, ctx) {
    return this.chooseCard!(view, legal, ctx);
  },

  chooseCard(view, legal, ctx) {
    const all = candidates(view, legal, ctx);
    if (all.length === 0) throw new Error('No legal action');
    if (chance(ctx, ctx.profile.mistakeRate)) {
      const pick = all[ctx.rng.int(all.length)]!;
      return { type: 'play', card: pick.card, capture: pick.capture };
    }
    const best = all.reduce((b, c) => (c.score > b.score ? c : b));
    return { type: 'play', card: best.card, capture: best.capture };
  },

  evaluateState(view) {
    const me = view.seat;
    if (me === null) return 0;
    const team = me % 2;
    const diff = view.teamScores[team]! - view.teamScores[1 - team]!;
    return Math.max(0, Math.min(1, 0.5 + diff / 22));
  },

  calculateRisk() {
    return 0;
  },

  handleTimeout(_view, legal) {
    const m = legal.moves.find((x) => x.captures.length > 0) ?? legal.moves[0]!;
    return { type: 'play', card: m.card, capture: m.captures[0] ?? [] };
  },
};
